import React, { useState, useEffect, useRef } from 'react';
import Peer from 'peerjs';
import { Html5Qrcode } from 'html5-qrcode';

export default function MobileQRScanner({ peerId }) {
  const [status, setStatus] = useState('connecting'); // connecting, connected, scanning, success, error
  const [error, setError] = useState(null);
  const [scannedData, setScannedData] = useState(null);
  const connRef = useRef(null);
  const scannerRef = useRef(null);
  const peerRef = useRef(null);

  useEffect(() => {
    // Connect to desktop peer
    const peer = new Peer();
    peerRef.current = peer;

    peer.on('open', () => {
      const conn = peer.connect(peerId);
      connRef.current = conn;

      conn.on('open', () => {
        setStatus('connected');
        setTimeout(() => startScanner(), 500);
      });

      conn.on('error', (err) => {
        console.error('Connection error:', err);
        setError('Erreur de connexion au PC');
        setStatus('error');
      });
    });

    peer.on('error', (err) => {
      console.error('Peer error:', err);
      setError('Impossible de se connecter au PC. Vérifiez la connexion.');
      setStatus('error');
    });

    return () => {
      stopScanner();
      if (peerRef.current) peerRef.current.destroy();
    };
  }, [peerId]);

  const startScanner = async () => {
    try {
      setStatus('scanning');
      setError(null);

      // Wait for DOM
      await new Promise(resolve => setTimeout(resolve, 100));

      const html5QrCode = new Html5Qrcode("qr-reader");
      scannerRef.current = html5QrCode;

      // Configuration optimale pour scanner un QR code imprimé
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true
        }
      };

      // Utiliser la caméra arrière
      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          console.log('QR Code scanné:', decodedText.substring(0, 50) + '...');

          // Arrêter le scanner immédiatement
          html5QrCode.stop().then(() => {
            scannerRef.current = null;
          }).catch(() => {});

          setScannedData(decodedText);
          setStatus('success');

          // Envoyer les données au PC
          if (connRef.current && connRef.current.open) {
            connRef.current.send({
              type: 'charting-data',
              data: decodedText
            });
            console.log('Données envoyées au PC');
          } else {
            console.error('Connexion perdue');
            setError('Connexion au PC perdue');
          }
        },
        () => {} // Ignorer les échecs de scan
      );
    } catch (e) {
      console.error('Erreur scanner:', e);

      // Message d'erreur plus explicite
      let errorMsg = 'Erreur caméra';
      if (e.message?.includes('NotAllowedError') || e.message?.includes('Permission')) {
        errorMsg = 'Veuillez autoriser l\'accès à la caméra';
      } else if (e.message?.includes('NotFoundError')) {
        errorMsg = 'Aucune caméra trouvée';
      } else if (e.message?.includes('NotReadableError')) {
        errorMsg = 'Caméra déjà utilisée par une autre application';
      }

      setError(errorMsg);
      setStatus('error');
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current = null;
      } catch (e) {
        // Ignorer
      }
    }
  };

  const rescan = async () => {
    setScannedData(null);
    setError(null);
    await startScanner();
  };

  const retry = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-500 to-orange-600 flex flex-col">
      {/* Header */}
      <div className="p-4 text-white text-center">
        <h1 className="text-xl font-bold">Scanner QR Charting</h1>
        <p className="text-sm opacity-90">Scannez le QR du charting précédent (dernière page du PDF)</p>
      </div>

      {/* Status bar */}
      <div className="px-4 py-2">
        <div className={`rounded-xl p-3 text-center font-medium ${
          status === 'connecting' ? 'bg-yellow-100 text-yellow-800' :
          status === 'connected' ? 'bg-blue-100 text-blue-800' :
          status === 'scanning' ? 'bg-blue-100 text-blue-800' :
          status === 'success' ? 'bg-green-100 text-green-800' :
          'bg-red-100 text-red-800'
        }`}>
          {status === 'connecting' && (
            <div className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Connexion au PC...
            </div>
          )}
          {status === 'connected' && 'Démarrage de la caméra...'}
          {status === 'scanning' && (
            <div className="flex items-center justify-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
              Recherche du QR code...
            </div>
          )}
          {status === 'success' && (
            <div className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Données envoyées au PC!
            </div>
          )}
          {status === 'error' && (
            <div className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Camera View */}
      <div className="flex-1 p-4">
        <div className="relative rounded-2xl overflow-hidden bg-black shadow-2xl" style={{ minHeight: '400px' }}>
          {/* Scanner container */}
          <div
            id="qr-reader"
            className="w-full h-full"
            style={{
              display: (status === 'scanning' || status === 'connected') ? 'block' : 'none',
              minHeight: '400px'
            }}
          />

          {/* Cadre de scan */}
          {status === 'scanning' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-64 h-64 border-4 border-amber-400 rounded-2xl">
                <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg"></div>
                <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg"></div>
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg"></div>
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg"></div>
              </div>
            </div>
          )}

          {/* Success overlay */}
          {status === 'success' && (
            <div className="absolute inset-0 bg-green-500/95 flex items-center justify-center">
              <div className="text-white text-center p-6">
                <svg className="w-24 h-24 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-2xl font-bold">Scan réussi!</p>
                <p className="text-sm opacity-90 mt-2">Les données ont été envoyées au PC</p>
                <p className="text-sm opacity-75 mt-4">Vous pouvez fermer cette page</p>
              </div>
            </div>
          )}

          {/* Error overlay */}
          {status === 'error' && (
            <div className="absolute inset-0 bg-red-500/95 flex items-center justify-center">
              <div className="text-white text-center p-6">
                <svg className="w-20 h-20 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-xl font-bold">Erreur</p>
                <p className="text-sm opacity-90 mt-2">{error}</p>
              </div>
            </div>
          )}

          {/* Connecting overlay */}
          {status === 'connecting' && (
            <div className="absolute inset-0 bg-amber-500/95 flex items-center justify-center">
              <div className="text-white text-center p-6">
                <svg className="w-20 h-20 mx-auto mb-4 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                </svg>
                <p className="text-xl font-bold">Connexion au PC...</p>
                <p className="text-sm opacity-90 mt-2">Veuillez patienter</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tips */}
      {status === 'scanning' && (
        <div className="px-4 pb-2">
          <div className="bg-white/20 rounded-xl p-4 text-white">
            <p className="font-bold mb-2">Conseils pour un scan réussi:</p>
            <ul className="text-sm space-y-1 opacity-90">
              <li>• Tenez le téléphone stable, parallèle au QR code</li>
              <li>• Assurez un bon éclairage (évitez les reflets)</li>
              <li>• Centrez le QR code dans le cadre</li>
              <li>• Éloignez-vous si le QR code est grand</li>
            </ul>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="p-4 space-y-3">
        {status === 'success' && (
          <button
            onClick={rescan}
            className="w-full py-4 bg-white text-amber-600 rounded-xl font-bold shadow-lg text-lg"
          >
            Scanner un autre QR code
          </button>
        )}

        {status === 'error' && (
          <button
            onClick={retry}
            className="w-full py-4 bg-white text-red-600 rounded-xl font-bold shadow-lg text-lg"
          >
            Réessayer
          </button>
        )}

        {(status === 'scanning' || status === 'connected') && (
          <button
            onClick={() => window.close()}
            className="w-full py-3 bg-white/20 text-white rounded-xl font-medium"
          >
            Annuler
          </button>
        )}
      </div>
    </div>
  );
}
